import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { PointerEvent as ReactPointerEvent, Ref, VideoHTMLAttributes } from "react"
import { cn } from "@vc/lib/utils"
import { computeZoomMetrics, type ZoomMetrics } from "@/features/meeting/services/screen-share-zoom"

interface ZoomPanVideoProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "ref" | "style" | "className"> {
  /** 1 = whole frame fits the box; above 1 the frame overflows and can be panned. */
  zoom: number
  videoRef: Ref<HTMLVideoElement>
  className?: string
  /**
   * Fires whenever the fit size or the source's own resolution changes —
   * everything the "100%" (actual size) toggle needs to decide whether to
   * show itself and what zoom level to ask for. This component already
   * tracks both (the viewport via ResizeObserver, the source via `resize`),
   * so this is a read of existing state rather than a second measurement
   * system running in parallel.
   */
  onMetricsChange?: (metrics: ZoomMetrics) => void
  /** A new stream/track was attached (`loadedmetadata`) — a different share
   *  started, as opposed to the same one resizing. */
  onSourceChange?: () => void
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value)
  else if (ref) (ref as { current: T | null }).current = value
}

/**
 * A shared screen that can be zoomed AND looked around in.
 *
 * Zoom used to be `transform: scale()` on the `<video>` inside an
 * `overflow-hidden` box. A transform does not change layout size, so the box
 * never grew: zooming in cut the edges off and there was nothing to scroll to
 * reach them. Here the video is laid out at its zoomed size inside a scroll
 * viewport, so the overflow is real — scrollbars, wheel/trackpad and touch
 * scrolling all reach every edge, and a mouse can drag the content around the
 * way Zoom's desktop client does. Each zoom step keeps the point at the centre
 * of the view where it was.
 *
 * Sized from the frame's own aspect (object-contain equivalent), not from the
 * box, so zooming a 16:9 screen in a tall box does not scroll into letterbox.
 */
export function ZoomPanVideo({
  zoom, videoRef, className, onMetricsChange, onSourceChange, ...videoProps
}: ZoomPanVideoProps) {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null)
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  const [box, setBox] = useState({ w: 0, h: 0 })
  // Raw decoded size, not just aspect — the "100%" toggle needs the actual
  // pixel count to know how far above Fit that is, which an aspect ratio
  // alone cannot say. computeZoomMetrics derives aspect from this itself.
  const [videoSize, setVideoSize] = useState({ w: 0, h: 0 })
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<{ id: number; x: number; y: number; left: number; top: number } | null>(null)
  // Point at the centre of the view, as a fraction of the content size.
  const centreRef = useRef({ x: 0.5, y: 0.5 })

  const setVideoEl = useCallback(
    (el: HTMLVideoElement | null) => {
      setVideo(el)
      assignRef(videoRef, el)
    },
    [videoRef],
  )

  // Outer size, scrollbars included, so a scrollbar appearing does not refit
  // the frame and nudge the layout it was added for.
  useEffect(() => {
    if (!viewport) return
    const measure = () => setBox({ w: viewport.offsetWidth, h: viewport.offsetHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(viewport)
    return () => ro.disconnect()
  }, [viewport])

  // `resize` fires when the presenter's resolution changes mid-share.
  useEffect(() => {
    if (!video) return
    const read = () => setVideoSize({ w: video.videoWidth, h: video.videoHeight })
    read()
    // Only `loadedmetadata` means a new source actually loaded — `resize` and
    // `emptied` are the same source resizing or clearing, not a different
    // one arriving. Firing onSourceChange for those would reset the "100%"
    // toggle's pin on every resize, which is the opposite of the point.
    const onLoaded = () => { read(); onSourceChange?.() }
    video.addEventListener("loadedmetadata", onLoaded)
    video.addEventListener("resize", read)
    video.addEventListener("emptied", read)
    return () => {
      video.removeEventListener("loadedmetadata", onLoaded)
      video.removeEventListener("resize", read)
      video.removeEventListener("emptied", read)
    }
  }, [video, onSourceChange])

  const metrics = computeZoomMetrics({
    boxWidth: box.w,
    boxHeight: box.h,
    videoWidth: videoSize.w,
    videoHeight: videoSize.h,
    devicePixelRatio: typeof window === "undefined" ? 1 : window.devicePixelRatio,
  })
  const { fitWidth: fitW, fitHeight: fitH } = metrics

  // Read, not a dependency-driven effect: metrics are cheap to recompute and
  // this keeps the callback and the render that produced it in the same
  // commit, rather than one tick behind it.
  useEffect(() => {
    onMetricsChange?.(metrics)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics.fitWidth, metrics.fitHeight, metrics.naturalZoom, metrics.canViewAtNaturalSize])

  const w = Math.round(fitW * zoom)
  const h = Math.round(fitH * zoom)
  const canPan = w > box.w + 1 || h > box.h + 1

  useLayoutEffect(() => {
    if (!viewport) return
    if (!canPan) {
      centreRef.current = { x: 0.5, y: 0.5 }
      return
    }
    const { x, y } = centreRef.current
    viewport.scrollLeft = x * viewport.scrollWidth - viewport.clientWidth / 2
    viewport.scrollTop = y * viewport.scrollHeight - viewport.clientHeight / 2
  }, [viewport, w, h, canPan])

  const onScroll = useCallback(() => {
    if (!viewport || !canPan) return
    centreRef.current = {
      x: (viewport.scrollLeft + viewport.clientWidth / 2) / viewport.scrollWidth,
      y: (viewport.scrollTop + viewport.clientHeight / 2) / viewport.scrollHeight,
    }
  }, [viewport, canPan])

  // Mouse only: touch and pen already pan the scroll viewport natively.
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!viewport || !canPan || e.pointerType !== "mouse" || e.button !== 0) return
    // A press on a scrollbar belongs to the scrollbar.
    const rect = viewport.getBoundingClientRect()
    if (e.clientX - rect.left >= viewport.clientWidth || e.clientY - rect.top >= viewport.clientHeight) return
    e.preventDefault()
    viewport.setPointerCapture(e.pointerId)
    dragRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      left: viewport.scrollLeft,
      top: viewport.scrollTop,
    }
    setDragging(true)
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!viewport || !d || d.id !== e.pointerId) return
    viewport.scrollLeft = d.left - (e.clientX - d.x)
    viewport.scrollTop = d.top - (e.clientY - d.y)
  }
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.id !== e.pointerId) return
    dragRef.current = null
    setDragging(false)
  }

  const sized = box.w > 0 && box.h > 0

  return (
    <div
      ref={setViewport}
      onScroll={onScroll}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={cn(
        "absolute inset-0",
        canPan ? "overflow-auto" : "overflow-hidden",
        canPan && (dragging ? "cursor-grabbing select-none" : "cursor-grab"),
        className,
      )}
    >
      {/* w-max + min-full: as wide as the frame when it overflows, centred
          when it does not. A plain full-width box would centre an oversized
          frame by pushing its left edge out of reach of the scrollbar. */}
      <div className="grid min-h-full min-w-full w-max place-items-center">
        <video
          {...videoProps}
          ref={setVideoEl}
          draggable={false}
          className={cn("block object-contain", !sized && "h-full w-full")}
          style={sized ? { width: w, height: h } : undefined}
        />
      </div>
    </div>
  )
}
